<template>
  <v-card
    class="mb-3"
    variant="outlined"
  >
    <v-card-text class="pb-2">
      <div class="d-flex align-center flex-wrap ga-2 cursor-pointer" @click="selectServer">
        <v-icon :color="statusColor">{{ statusIcon }}</v-icon>
        <div class="text-subtitle-1 font-weight-medium">{{ server.displayName }}</div>
        <v-chip
          v-if="showAvailabilityChip"
          size="small"
          color="default"
          variant="tonal"
        >
          {{ installLabel }}
        </v-chip>
        <v-spacer />
        <v-btn size="small" variant="text" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click.stop="toggleExpanded" />
      </div>

      <v-expand-transition>
        <div v-if="expanded" class="mt-3">
          <div class="text-caption text-medium-emphasis mb-1">Server URL</div>
          <div class="text-body-2 mb-3 break-url">{{ server.url || 'Platform-managed server' }}</div>

          <div class="text-caption text-medium-emphasis mb-1">Description</div>
          <div class="text-body-2 mb-3">{{ server.description || 'No description provided.' }}</div>

          <div class="text-caption text-medium-emphasis mb-1">Secrets</div>
          <div v-if="showSecretSummary" class="text-body-2 mb-3">{{ secretSummary }}</div>

          <div v-if="renderedSecretFields.length > 0" class="mb-3">
            <v-alert
              v-if="saveError"
              type="error"
              density="compact"
              variant="tonal"
              class="mb-3"
            >
              {{ saveError }}
            </v-alert>
            <div
              v-for="field in renderedSecretFields"
              :key="field.name"
              class="mb-3"
            >
              <div class="text-caption text-medium-emphasis mb-1">
                {{ field.label }}
                <span v-if="field.required" class="text-error">*</span>
              </div>
              <div v-if="field.description" class="text-body-2 mb-2">{{ field.description }}</div>
              <v-text-field
                v-model="secretValues[field.name]"
                :label="field.label"
                type="password"
                variant="outlined"
                density="compact"
              />
            </div>
          </div>

          <div v-if="showExpandedAuthLabel" class="mb-3">
            <div class="text-caption text-medium-emphasis mb-1">OAuth</div>
            <div class="text-body-2">{{ authLabel }}</div>
          </div>

          <div class="d-flex flex-wrap ga-2">
            <v-btn size="small" variant="outlined" @click.stop="selectServer">View Tools</v-btn>
            <v-btn
              v-if="server.canConfigure && renderedSecretFields.length > 0"
              size="small"
              color="primary"
              variant="outlined"
              :loading="savingSecrets"
              @click.stop="saveSecrets"
            >
              Save Secrets
            </v-btn>
            <v-btn
              v-if="server.authMode === 'oauth2' && server.installed"
              size="small"
              color="primary"
              variant="outlined"
              @click.stop="openOAuthDialog"
            >
              {{ oauthActionLabel }}
            </v-btn>
            <v-btn
              v-if="server.canInstall && !server.installed"
              size="small"
              color="primary"
              variant="outlined"
              @click.stop="toggleInstalled"
            >
              Install
            </v-btn>
            <v-btn
              v-if="server.canUninstall && server.installed && !server.enabled"
              size="small"
              color="error"
              variant="outlined"
              @click.stop="toggleInstalled"
            >
              Uninstall
            </v-btn>
            <v-btn
              v-if="server.source === 'external' && server.installed"
              size="small"
              variant="outlined"
              @click.stop="toggleEnabled"
            >
              {{ server.enabled ? 'Disable' : 'Enable' }}
            </v-btn>
            <v-btn
              v-if="isAdmin && server.source === 'external'"
              size="small"
              color="error"
              variant="outlined"
              @click.stop="deleteSharedServer"
            >
              Delete Shared
            </v-btn>
            <v-btn
              v-else-if="!isAdmin && server.source === 'external' && !server.installed"
              size="small"
              color="warning"
              variant="outlined"
              @click.stop="requestSharedRemoval"
            >
              Request Removal
            </v-btn>
          </div>
        </div>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { UserVisibleMcpServer } from '@/types/mcp'
import { isApiError } from '@/types/api'
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'

@Options({
  props: {
    server: Object,
    selected: Boolean
  },
  emits: ['select', 'install', 'uninstall', 'enable', 'disable', 'connectOAuth', 'saveSecrets', 'deleteShared', 'requestRemoval'],
  computed: {
    ...mapGetters(['isAdmin'])
  },
  watch: {
    server: {
      handler() {
        this.syncSecretValues()
      },
      deep: true,
      immediate: true
    }
  }
})
export default class UserMcpServerCard extends Vue {
  private static readonly SECRET_MASK = '••••••'

  server!: UserVisibleMcpServer
  selected!: boolean
  isAdmin!: boolean
  expanded = false
  savingSecrets = false
  saveError = ''
  secretValues: Record<string, string> = {}

  get installLabel(): string {
    return 'Available'
  }

  get showAvailabilityChip(): boolean {
    return this.server.source === 'external' && this.server.installed !== true
  }

  get authLabel(): string {
    if (this.server.authMode !== 'oauth2') {
      return 'No OAuth Required'
    }
    if (this.server.authState === 'connected') {
      return 'Connected'
    }
    if (this.server.authState === 'reauth_required') {
      return 'Reconnect OAuth'
    }
    if (this.server.authState === 'error') {
      return 'OAuth Error'
    }
    return 'Connect OAuth'
  }

  get oauthActionLabel(): string {
    return this.server.authState === 'reauth_required' ? 'Reconnect OAuth' : 'Connect OAuth'
  }

  get showExpandedAuthLabel(): boolean {
    return this.server.authMode === 'oauth2'
  }

  get statusColor(): string {
    if (this.server.source === 'platform') {
      return this.server.enabled ? 'success' : 'warning'
    }
    if (!this.server.installed) return 'default'
    if (this.server.authMode === 'oauth2' && this.server.authState !== 'connected') return 'warning'
    return this.server.enabled ? 'success' : 'warning'
  }

  get statusIcon(): string {
    if (this.server.authMode === 'oauth2' && this.server.installed && this.server.authState !== 'connected') {
      return 'mdi-shield-alert-outline'
    }
    return this.server.enabled ? 'mdi-checkbox-marked-circle-outline' : 'mdi-pause-circle-outline'
  }

  get secretSummary(): string {
    const requiredCount = this.renderedSecretFields.length
    const configuredCount = this.configuredVisibleSecretNames.length

    if (requiredCount === 0) {
      return 'No user secrets required'
    }

    if (configuredCount > 0) {
      return `${configuredCount} user secret${configuredCount === 1 ? '' : 's'} configured`
    }

    return `${requiredCount} user secret${requiredCount === 1 ? '' : 's'} required`
  }

  get showSecretSummary(): boolean {
    return this.renderedSecretFields.length === 0
  }

  get renderedSecretFields(): Array<{
    name: string
    label: string
    description: string
    required: boolean
    inputType: 'password'
  }> {
    if ((this.server.secretFields?.length || 0) > 0) {
      return this.server.secretFields
    }

    return (this.server.secretNames || []).map((name) => ({
      name,
      label: name,
      description: '',
      required: true,
      inputType: 'password' as const
    }))
  }

  get configuredVisibleSecretNames(): string[] {
    const renderedNames = new Set(this.renderedSecretFields.map((field) => field.name))
    return (this.server.configuredSecretNames || []).filter((name) => renderedNames.has(name))
  }

  created() {
    this.syncSecretValues()
  }

  syncSecretValues(): void {
    const nextValues: Record<string, string> = {}
    const configuredNames = new Set(this.configuredVisibleSecretNames)

    for (const field of this.renderedSecretFields) {
      const existingValue = this.secretValues[field.name]
      if (existingValue && existingValue !== UserMcpServerCard.SECRET_MASK) {
        nextValues[field.name] = existingValue
        continue
      }

      nextValues[field.name] = configuredNames.has(field.name) ? UserMcpServerCard.SECRET_MASK : ''
    }

    this.secretValues = nextValues
  }

  private getPendingSecretsPayload(): Array<{ name: string; value: string }> {
    return Object.entries(this.secretValues)
      .filter(([, value]) => value.trim().length > 0 && value !== UserMcpServerCard.SECRET_MASK)
      .map(([name, value]) => ({ name, value }))
  }

  private getMissingRequiredSecretLabels(): string[] {
    const configuredNames = new Set(this.configuredVisibleSecretNames)
    return this.renderedSecretFields
      .filter((field) => {
        if (!field.required) {
          return false
        }
        if (configuredNames.has(field.name)) {
          return false
        }
        return !(this.secretValues[field.name] || '').trim()
      })
      .map((field) => field.label || field.name)
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded
    if (this.expanded) {
      this.$emit('select', this.server.name)
    }
  }

  async toggleInstalled(): Promise<void> {
    if (this.server.installed) {
      this.$emit('uninstall', this.server.name)
      return
    }
    this.$emit('install', this.server.name)
  }

  async toggleEnabled(): Promise<void> {
    if (this.server.enabled) {
      this.$emit('disable', this.server.name)
      return
    }
    this.$emit('enable', this.server.name)
  }

  async saveSecrets(): Promise<void> {
    if (this.renderedSecretFields.length === 0) {
      return
    }

    const missingRequiredLabels = this.getMissingRequiredSecretLabels()
    if (missingRequiredLabels.length > 0) {
      this.saveError = `Missing required secrets: ${missingRequiredLabels.join(', ')}`
      return
    }

    const secrets = this.getPendingSecretsPayload()

    this.savingSecrets = true
    this.saveError = ''

    try {
      await this.$store.dispatch('saveExternalServerSecrets', {
        serverName: this.server.name,
        secrets
      })
    } catch (error) {
      this.saveError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to save secrets'
    } finally {
      this.savingSecrets = false
    }
  }

  async openOAuthDialog(): Promise<void> {
    const missingRequiredLabels = this.getMissingRequiredSecretLabels()
    if (missingRequiredLabels.length > 0) {
      this.saveError = `Missing required secrets: ${missingRequiredLabels.join(', ')}`
      return
    }

    const secrets = this.getPendingSecretsPayload()
    if (secrets.length > 0) {
      this.savingSecrets = true
      this.saveError = ''
      try {
        await this.$store.dispatch('saveExternalServerSecrets', {
          serverName: this.server.name,
          secrets
        })
        this.secretValues = {}
      } catch (error) {
        this.saveError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to save secrets'
        return
      } finally {
        this.savingSecrets = false
      }
    }

    this.$emit('connectOAuth', this.server.name)
  }

  selectServer(): void {
    this.$emit('select', this.server.name)
  }

  deleteSharedServer(): void {
    this.$emit('deleteShared', this.server.name)
  }

  requestSharedRemoval(): void {
    this.$emit('requestRemoval', this.server.name)
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.break-url {
  overflow-wrap: anywhere;
}
</style>
